import type { CrmVersion, CrmVersionenListe } from '@portal/contracts';
import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { onLinkClick } from '../../router.js';
import { AKTEUR_LABEL, AKTION_LABEL, LEER, TABELLE_LABEL, akteurTeile, feldLabel, feldText, formatZeit, istZeile, kundenPfad, kurzId, wertText } from './crm-felder.js';

/** jsonb-Wert lesbar: Einzelwert als Text, ganze Zeile (insert/delete) einklappbar */
export function Wert({ v, titel }: { v: unknown; titel: string }) {
  if (!istZeile(v)) return <>{wertText(v)}</>;
  const eintraege = Object.entries(v).filter(([, x]) => x !== null && x !== '' && !(Array.isArray(x) && x.length === 0));
  return (
    <details className="crm-zeile">
      <summary>
        <ChevronDown size={14} aria-hidden /> {titel} ({eintraege.length} {eintraege.length === 1 ? 'Feld' : 'Felder'})
      </summary>
      <dl className="crm-zeile__felder">
        {eintraege.map(([k, x]) => (
          <div key={k}>
            <dt>{feldLabel(k)}</dt>
            <dd>{feldText(k, x)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * Tabelle des Änderungsprotokolls (chronologisch absteigend, wie die API liefert): Wann · Feld · Alt → Neu · Wer ·
 * Akteur-Typ · Quelle (Tabelle/Aktion/IP/Request-ID dezent). Nichts hier kann Versionen ändern oder löschen.
 */
/** personen: id → Name (null = Person gibt es nicht mehr, 404); fehlt der Eintrag, ist der Name (noch) nicht geladen */
export function VersionenTabelle({ daten, mitPerson, personen }: { daten: CrmVersionenListe; mitPerson?: boolean; personen?: ReadonlyMap<string, string | null> }) {
  const leer = daten.eintraege.length === 0;
  return (
    <div className={`pb-card crm-tabelle-wrap${leer ? ' crm-tabelle-wrap--leer' : ''}`}>
      <table className="crm-tabelle crm-versionen">
        <caption className="sr-only">Änderungsprotokoll mit Zeitpunkt, Feld, altem und neuem Wert, Akteur und Quelle</caption>
        <thead>
          <tr>
            <th scope="col">Wann</th>
            {mitPerson && <th scope="col">Person</th>}
            <th scope="col">Feld</th>
            <th scope="col">Alt → Neu</th>
            <th scope="col">Wer</th>
            <th scope="col">Akteur-Typ</th>
            <th scope="col">Quelle</th>
          </tr>
        </thead>
        <tbody>
          {leer ? (
            <tr className="crm-leer">
              <td colSpan={mitPerson ? 7 : 6}>Keine Einträge im Änderungsprotokoll.</td>
            </tr>
          ) : (
            daten.eintraege.map((v) => <Zeile key={v.id} v={v} mitPerson={mitPerson} personen={personen} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function Zeile({ v, mitPerson, personen }: { v: CrmVersion; mitPerson?: boolean; personen?: ReadonlyMap<string, string | null> }) {
  const ganz = v.aktion !== 'update';
  let person: ReactNode = LEER;
  if (mitPerson) {
    const name = v.personId ? personen?.get(v.personId) : undefined;
    person = !v.personId ? (
      LEER
    ) : name === null ? (
      <span className="crm-muted" title={v.personId}>
        gelöscht
      </span>
    ) : name === undefined ? (
      <a href={kundenPfad(v.personId)} onClick={onLinkClick(kundenPfad(v.personId))} className="crm-tech" title={v.personId}>
        {kurzId(v.personId)}
      </a>
    ) : (
      <a href={kundenPfad(v.personId)} onClick={onLinkClick(kundenPfad(v.personId))} className="crm-name">
        {name || 'Ohne Namen'}
      </a>
    );
  }
  return (
    <tr>
      <td data-label="Wann" className="pb-num crm-nowrap">
        {formatZeit(v.zeitpunkt)}
      </td>
      {mitPerson && <td data-label="Person">{person}</td>}
      <td data-label="Feld">{ganz ? <span className="crm-muted">{AKTION_LABEL[v.aktion] ?? v.aktion}</span> : feldLabel(v.feld)}</td>
      <td data-label="Alt → Neu" className="crm-diff">
        <span className="crm-zelle">
          {ganz ? (
            <Wert v={v.aktion === 'delete' ? v.wertAlt : v.wertNeu} titel={v.aktion === 'delete' ? 'Gelöschte Zeile' : 'Angelegte Zeile'} />
          ) : (
            <>
              <span className="crm-diff__alt" title={typeof v.wertAlt === 'string' ? v.wertAlt : undefined}>
                {feldText(v.feld ?? '', v.wertAlt)}
              </span>
              <span className="crm-diff__pfeil" aria-hidden>
                {' → '}
              </span>
              <span className="sr-only"> wird zu </span>
              <span className="crm-diff__neu" title={typeof v.wertNeu === 'string' ? v.wertNeu : undefined}>
                {feldText(v.feld ?? '', v.wertNeu)}
              </span>
            </>
          )}
          {v.kommentar && <span className="crm-kommentar">„{v.kommentar}“</span>}
        </span>
      </td>
      <td data-label="Wer" className="crm-wer">
        <Akteur wert={v.geaendertVon} />
      </td>
      <td data-label="Akteur-Typ">{AKTEUR_LABEL[v.akteurTyp] ?? v.akteurTyp}</td>
      <td data-label="Quelle">
        <span className="crm-zelle crm-quelle">
          <span>
            {TABELLE_LABEL[v.tabelle] ?? v.tabelle} · {AKTION_LABEL[v.aktion] ?? v.aktion}
          </span>
          <span className="crm-quelle__meta">
            IP {v.quelleIp ?? LEER} · Request {v.requestId ?? LEER}
          </span>
          <span className="crm-quelle__meta crm-tech">
            Version #{v.id} · Datensatz <span title={v.datensatzId}>{kurzId(v.datensatzId)}</span>
          </span>
        </span>
      </td>
    </tr>
  );
}

/** „Wer“: Anzeigename prominent, Kennung dezent und gekürzt (volle ID im title); Agent/Workflow/Import ohne Klammer unverändert */
function Akteur({ wert }: { wert: string }) {
  const { name, id } = akteurTeile(wert);
  return (
    <span className="crm-akteur" title={wert}>
      <span className="crm-akteur__name">{name}</span>
      {id && (
        <span className="crm-akteur__id crm-tech" aria-label={`Kennung ${id}`}>
          {kurzId(id)}
        </span>
      )}
    </span>
  );
}

/** Seitenumschaltung („Seite x von y“) */
export function Seiten({ seite, groesse, gesamt, onSeite }: { seite: number; groesse: number; gesamt: number; onSeite(s: number): void }) {
  const letzte = Math.max(1, Math.ceil(gesamt / groesse));
  return (
    <nav className="crm-seiten" aria-label="Seiten">
      <span className="crm-muted" role="status">
        {gesamt} {gesamt === 1 ? 'Eintrag' : 'Einträge'} · Seite {seite} von {letzte}
      </span>
      <button type="button" className="pb-button pb-button--quiet" disabled={seite <= 1} onClick={() => onSeite(seite - 1)}>
        Zurück
      </button>
      <button type="button" className="pb-button pb-button--quiet" disabled={seite >= letzte} onClick={() => onSeite(seite + 1)}>
        Weiter
      </button>
    </nav>
  );
}

/** Filterfeld (Text) mit Übernahme bei Enter/Blur */
export function Filter({ label, wert, onChange, typ = 'text' }: { label: string; wert: string; onChange(v: string): void; typ?: 'text' | 'datetime-local' }) {
  const id = useId();
  const [lokal, setLokal] = useState(wert);
  return (
    <div className="crm-feld crm-filter">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={typ}
        value={lokal}
        onChange={(e) => setLokal(e.target.value)}
        onBlur={() => lokal !== wert && onChange(lokal)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onChange(lokal);
          }
        }}
      />
    </div>
  );
}
